import React, { useState } from 'react';
import { Eye, EyeOff, Lock, Shield, Check, X, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { changePassword } from '../profileAPIs';

interface PasswordFormData {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}

interface PasswordStrength {
    score: number;
    feedback: string[];
    color: string;
    label: string;
}

interface changePasswordRes {
    message: string;
}

export default function ChangePassword() {
    const [formData, setFormData] = useState<PasswordFormData>({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });

    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState<Partial<PasswordFormData>>({});

    // Password strength checker
    const checkPasswordStrength = (password: string): PasswordStrength => {
        let score = 0;
        const feedback: string[] = [];

        if (password.length >= 8) {
            score += 1;
        } else {
            feedback.push('At least 8 characters');
        }

        if (/[a-z]/.test(password)) {
            score += 1;
        } else {
            feedback.push('One lowercase letter');
        }

        if (/[A-Z]/.test(password)) {
            score += 1;
        } else {
            feedback.push('One uppercase letter');
        }

        if (/\d/.test(password)) {
            score += 1;
        } else {
            feedback.push('One number');
        }

        if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
            score += 1;
        } else {
            feedback.push('One special character');
        }

        const strengthMap = {
            0: { color: 'bg-gray-200', label: 'Very Weak' },
            1: { color: 'bg-red-400', label: 'Weak' },
            2: { color: 'bg-orange-400', label: 'Fair' },
            3: { color: 'bg-yellow-400', label: 'Good' },
            4: { color: 'bg-green-400', label: 'Strong' },
            5: { color: 'bg-green-600', label: 'Very Strong' }
        };

        const strength = strengthMap[score as keyof typeof strengthMap];

        return {
            score,
            feedback,
            color: strength.color,
            label: strength.label
        };
    };

    const passwordStrength = checkPasswordStrength(formData.newPassword);

    // Handle input changes
    const handleInputChange = (field: keyof PasswordFormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        
        // Clear errors when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    // Toggle password visibility
    const togglePasswordVisibility = (field: keyof typeof showPasswords) => {
        setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
    };

    // Validate form
    const validateForm = (): boolean => {
        const newErrors: Partial<PasswordFormData> = {};

        if (!formData.currentPassword) {
            newErrors.currentPassword = 'Current password is required';
        }

        if (!formData.newPassword) {
            newErrors.newPassword = 'New password is required';
        } else if (formData.newPassword.length < 8) {
            newErrors.newPassword = 'Password must be at least 8 characters long';
        } else if (passwordStrength.score < 3) {
            newErrors.newPassword = 'Password is too weak. Please choose a stronger password';
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your new password';
        } else if (formData.newPassword !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        if (formData.currentPassword === formData.newPassword) {
            newErrors.newPassword = 'New password must be different from current password';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        setIsLoading(true);
        const res = await changePassword(formData) as changePasswordRes;
        if (res.message === "Password created and stored successfully.") {
            toast.success("Password created and stored successfully.");
        }else if (res.message === "Old password is required to change your password.") {
            toast.error("Old password is required to change your password.");
        }else if (res.message === "Old password is incorrect.") {
            toast.error("Old password is incorrect.");
        }else if (res.message === "Password updated successfully.") {
            toast.success("Password updated successfully.");
        }else if (res.message === "Token does not contain a mentorId.") {
            toast.error("Invalid Token!");
        }
        setIsLoading(false);
    };

    return (
        <div className="flex-1">
            <div className="bg-white rounded-2xl shadow-sm p-8">
                {/* Header */}
                <div className="flex items-center gap-3 mb-8">
                    <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl flex items-center justify-center">
                        <Lock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Change Password</h2>
                        <p className="text-gray-600">Update your password to keep your account secure</p>
                    </div>
                </div>

                {/* Security Tips */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                    <div className="flex items-start gap-3">
                        <Shield className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div>
                            <h3 className="font-semibold text-blue-900 mb-2">Password Security Tips</h3>
                            <ul className="text-sm text-blue-700 space-y-1">
                                <li>• Use at least 8 characters with a mix of letters, numbers, and symbols</li>
                                <li>• Avoid using personal information like names or birthdays</li>
                                <li>• Don't reuse passwords from other accounts</li>
                                <li>• Consider using a password manager</li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Current Password */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Current Password *
                        </label>
                        <div className="relative">
                            <input
                                type={showPasswords.current ? 'text' : 'password'}
                                value={formData.currentPassword}
                                onChange={(e) => handleInputChange('currentPassword', e.target.value)}
                                className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all ${
                                    errors.currentPassword ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                }`}
                                placeholder="Enter your current password"
                            />
                            <button
                                type="button"
                                onClick={() => togglePasswordVisibility('current')}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                {showPasswords.current ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                        {errors.currentPassword && (
                            <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                                <X size={14} />
                                {errors.currentPassword}
                            </p>
                        )}
                    </div>

                    {/* New Password */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            New Password *
                        </label>
                        <div className="relative">
                            <input
                                type={showPasswords.new ? 'text' : 'password'}
                                value={formData.newPassword}
                                onChange={(e) => handleInputChange('newPassword', e.target.value)}
                                className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all ${
                                    errors.newPassword ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                }`}
                                placeholder="Enter your new password"
                            />
                            <button
                                type="button"
                                onClick={() => togglePasswordVisibility('new')}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                {showPasswords.new ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>

                        {/* Password Strength Indicator */}
                        {formData.newPassword && (
                            <div className="mt-3">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm text-gray-600">Password Strength</span>
                                    <span className={`text-sm font-medium ${
                                        passwordStrength.score >= 4 ? 'text-green-600' :
                                        passwordStrength.score >= 3 ? 'text-yellow-600' :
                                        passwordStrength.score >= 2 ? 'text-orange-600' : 'text-red-600'
                                    }`}>
                                        {passwordStrength.label}
                                    </span>
                                </div>
                                <div className="flex gap-1 mb-2">
                                    {[1, 2, 3, 4, 5].map((level) => (
                                        <div
                                            key={level}
                                            className={`h-2 flex-1 rounded-full transition-colors ${
                                                level <= passwordStrength.score 
                                                    ? passwordStrength.color 
                                                    : 'bg-gray-200'
                                            }`}
                                        />
                                    ))}
                                </div>
                                {passwordStrength.feedback.length > 0 && (
                                    <div className="text-sm text-gray-600">
                                        <span className="font-medium">Missing: </span>
                                        {passwordStrength.feedback.join(', ')}
                                    </div>
                                )}
                            </div>
                        )}

                        {errors.newPassword && (
                            <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                                <X size={14} />
                                {errors.newPassword}
                            </p>
                        )}
                    </div>

                    {/* Confirm Password */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Confirm New Password *
                        </label>
                        <div className="relative">
                            <input
                                type={showPasswords.confirm ? 'text' : 'password'}
                                value={formData.confirmPassword}
                                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                                className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all ${
                                    errors.confirmPassword ? 'border-red-300 bg-red-50' : 
                                    formData.confirmPassword && formData.newPassword === formData.confirmPassword ? 'border-green-300 bg-green-50' :
                                    'border-gray-300'
                                }`}
                                placeholder="Confirm your new password"
                            />
                            <button
                                type="button"
                                onClick={() => togglePasswordVisibility('confirm')}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                {showPasswords.confirm ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                            {formData.confirmPassword && formData.newPassword === formData.confirmPassword && (
                                <div className="absolute right-10 top-1/2 transform -translate-y-1/2">
                                    <Check size={20} className="text-green-500" />
                                </div>
                            )}
                        </div>
                        {errors.confirmPassword && (
                            <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                                <X size={14} />
                                {errors.confirmPassword}
                            </p>
                        )}
                        {!errors.confirmPassword && formData.confirmPassword && formData.newPassword === formData.confirmPassword && (
                            <p className="text-green-600 text-sm mt-1 flex items-center gap-1">
                                <Check size={14} />
                                Passwords match
                            </p>
                        )}
                    </div>

                    {/* Warning */}
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                            <div className="text-sm text-amber-700">
                                <p className="font-semibold mb-1">Important:</p>
                                <p>Changing your password will log you out of all other devices. You'll need to log in again with your new password.</p>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
                        <button
                            type="button"
                            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                            onClick={() => {
                                setFormData({
                                    currentPassword: '',
                                    newPassword: '',
                                    confirmPassword: ''
                                });
                                setErrors({});
                                setShowPasswords({
                                    current: false,
                                    new: false,
                                    confirm: false
                                });
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isLoading || !formData.currentPassword || !formData.newPassword || !formData.confirmPassword || passwordStrength.score < 3}
                            className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all shadow-lg flex items-center gap-2"
                        >
                            {isLoading ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Changing Password...
                                </>
                            ) : (
                                <>
                                    <Lock size={16} />
                                    Change Password
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}